/**
 * report-problems offline outbox (T35.6.3).
 *
 * Queues reports that could not reach the server (offline / 5xx). Each queued
 * item holds everything the server needs except screenshots — binary blobs are
 * NOT queued offline (they must be attached while the device is online per the
 * task spec). The hook `useReportSubmit` flushes on mount and on reconnect.
 *
 * Uses the raw IndexedDB browser global (no extra package). CP1: IndexedDB is a browser global — this
 * file lives in `apps/web`; `packages/shared/**` must stay platform-free.
 *
 * Storage: IndexedDB `shopista-report-outbox` / `pending` object store.
 * Key: `idempotencyKey` (the client UUID — also the dedup token sent to the
 * server so resends are recorded once regardless of how many retries).
 */

import type { ReportItemContext, ReportProblemSeverity } from './api';

const DB_NAME = 'shopista-report-outbox';
const DB_VERSION = 1;
const STORE = 'pending';

export interface QueuedReportProblem {
  /** Client-minted UUID — same value posted to the server for dedup. */
  idempotencyKey: string;
  note: string | null;
  severity: ReportProblemSeverity | null;
  screenContext: string | null;
  itemContext: ReportItemContext | null;
  env: { appBuild: string; device: string; online: boolean } | null;
  isConsentToContact: boolean | undefined;
  queuedAt: number; // Date.now() ms
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'idempotencyKey' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Failed to open report outbox'));
  });
}

function promisifyTx<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Outbox request failed'));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Outbox transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('Outbox transaction aborted'));
  });
}

/** Queue a report for later delivery (overwrites by key — re-queue is safe). */
export async function enqueueReportProblem(
  item: QueuedReportProblem,
): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await promisifyTx(tx.objectStore(STORE).put(item));
    await txDone(tx);
  } finally {
    db.close();
  }
}

/** All queued items, oldest first. */
export async function listQueuedReportProblems(): Promise<QueuedReportProblem[]> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    const all = await promisifyTx(
      tx.objectStore(STORE).getAll() as IDBRequest<QueuedReportProblem[]>,
    );
    return all.sort((a, b) => a.queuedAt - b.queuedAt);
  } finally {
    db.close();
  }
}

/** Remove one item by its key (called after a successful delivery). */
export async function removeQueuedReportProblem(idempotencyKey: string): Promise<void> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    await promisifyTx(tx.objectStore(STORE).delete(idempotencyKey));
    await txDone(tx);
  } finally {
    db.close();
  }
}

/** Count of pending items (useful for badge / empty-state checks). */
export async function countQueuedReportProblems(): Promise<number> {
  const db = await openDb();
  try {
    const tx = db.transaction(STORE, 'readonly');
    return await promisifyTx(tx.objectStore(STORE).count());
  } finally {
    db.close();
  }
}
